import { Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import red from '@material-ui/core/colors/red';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import {
  createStyles,
  createTheme,
  makeStyles,
  ThemeProvider,
} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import React from 'react';
import { theme } from 'styleguide/Theme/config';

const useStyles = makeStyles(() =>
  createStyles({
    iconPostion: {
      marginRight: '16px',
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    trimTopSpace: {
      paddingTop: '0px !important',
      marginTop: '0px',
    },
  })
);

const dialogTheme = createTheme({
  ...theme,
  overrides: {
    ...theme.overrides,
    MuiDialog: {
      paper: {
        padding: '16px',
      },
    },
    MuiDialogTitle: {
      root: {
        padding: '0',
        fontSize: '20px',
        fontWeight: 500,
        lineHeight: '24px',
      },
    },
    MuiDialogContent: {
      root: {
        padding: '0',
        marginTop: '16px',
      },
    },
    MuiDialogActions: {
      root: {
        padding: '0',
        marginTop: '16px',
      },
    },
  },
});

const dialogThemeNoPadding = createTheme({
  ...theme,
  overrides: {
    ...theme.overrides,
    MuiDialog: {
      paper: {
        padding: 0,
      },
    },
    MuiDialogTitle: {
      root: {
        padding: '0',
        fontSize: '20px',
        fontWeight: 500,
        lineHeight: '24px',
      },
    },
    MuiDialogContent: {
      root: {
        padding: '0',
        marginTop: '16px',
      },
    },
    MuiDialogActions: {
      root: {
        padding: '0',
        marginTop: '16px',
      },
    },
  },
});

const cancelButtonTheme = createTheme({
  palette: { primary: { main: red[500] } },
});

export type Props = {
  cancelButtonText?: string;
  children: React.ReactNode;
  hideCancelButton?: boolean;
  hideSubmitButton?: boolean;
  icon?: string;
  onCancel?: () => void;
  onSubmit?: () => void;
  open: boolean;
  submitButtonText?: string;
  title?: string;
  disableBackdropClick?: boolean;
  closeIcon?: boolean;
  submitButtonDisabled?: boolean;
  comment?: string;
  maxwidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  /** When true, removes padding from the dialog paper (e.g. for Add Break popup) */
  disablePaperPadding?: boolean;
  /** Custom styles for the action buttons area (CANCEL, ADD BREAK, etc.) */
  actionAreaStyle?: React.CSSProperties;
  /** Extra button rendered between the cancel and submit buttons. Omit to render nothing. */
  secondaryAction?: React.ReactNode;
};

const Alert: React.FC<Props> = ({
  children,
  icon,
  open,
  title = '',
  onCancel,
  onSubmit,
  submitButtonText = 'Continue',
  cancelButtonText = 'Cancel',
  hideCancelButton,
  hideSubmitButton,
  disableBackdropClick = true,
  closeIcon = false,
  submitButtonDisabled,
  comment,
  maxwidth = 'sm',
  disablePaperPadding = false,
  actionAreaStyle,
  secondaryAction,
}) => {
  const classes = useStyles();
  const shouldHideTitle = title === '';
  const themeToUse = disablePaperPadding ? dialogThemeNoPadding : dialogTheme;
  return (
    <ThemeProvider theme={themeToUse}>
      <Dialog
        fullWidth
        open={open}
        disablePortal
        disableBackdropClick={disableBackdropClick}
        scroll="body"
        onClose={onCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth={maxwidth}
      >
        {shouldHideTitle || (
          <DialogTitle id="alert-dialog-title" disableTypography>
            <Box display="flex" alignItems="center">
              {icon && <Icon className={classes.iconPostion}>{icon}</Icon>}
              {title}
              {closeIcon && (
                <IconButton
                  aria-label="close"
                  className={classes.closeButton}
                  onClick={onCancel}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </DialogTitle>
        )}
        <DialogContent
          classes={shouldHideTitle ? { root: classes.trimTopSpace } : {}}
        >
          {children}
        </DialogContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="baseline"
          style={actionAreaStyle}
        >
          <Box>
            <Typography variant="body2" color="textSecondary">
              {comment}
            </Typography>
          </Box>
          <Box>
            {(!hideCancelButton || !hideSubmitButton || secondaryAction) && (
              <DialogActions>
                {!hideCancelButton && (
                  <ThemeProvider theme={cancelButtonTheme}>
                    <Button
                      disableElevation
                      color="primary"
                      onClick={onCancel}
                      variant="outlined"
                    >
                      {cancelButtonText}
                    </Button>
                  </ThemeProvider>
                )}
                {secondaryAction}
                {!hideSubmitButton && (
                  <Button
                    disableElevation
                    onClick={onSubmit}
                    variant="contained"
                    color="primary"
                    disabled={submitButtonDisabled}
                  >
                    {submitButtonText}
                  </Button>
                )}
              </DialogActions>
            )}
          </Box>
        </Box>
      </Dialog>
    </ThemeProvider>
  );
};
export default React.memo(Alert);
